import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FieldValues, Path, useFieldArray, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
//
import { useContextGlobal } from '../../../context/ContextGlobal';
import { useToast } from '../../../context/ToastContext';
import api from '../../../services/api';
import {
    handleExceptionMessage,
    handleExceptionMultipleMessages,
} from '../../../util/handleExceptionAxios';
import { message } from '../../../util/handleMessages';
import AlertDialog from '../../dialog/alertDialog';
import { IconComponent, icons } from '../../icons';
import {
    IFormProps,
    ImperativeHandleAccessedByParentComponent,
} from '../../interfaces/formProps';
import { FormButton } from '../formButton';
import { FormInputProps } from '../formInterfaces';
import RenderForm from '../renderForm';

import { formatNumber } from '../../../util/infoFormat';
import { useWindowSize } from '../../../util/responsiveness';
import { SubFormComplete } from '../subFormComplete';
import './styles.scss';

const AdditionalDataFooter: React.FC<{ data: any }> = props => {
    const { data } = props;

    if (data != null) {
        return (
            <Typography id="total" component="div">
                {`Total: ${formatNumber(data)}`}
            </Typography>
        );
    }
    return <></>;
};

const FormComplete = React.forwardRef(
    <T extends FieldValues>(
        props: IFormProps<T>,
        ref?: React.Ref<ImperativeHandleAccessedByParentComponent<T>>,
    ) => {
        const {
            settings,
            defaultValues,
            inputsForm,
            implSetValueData,
            callbackSuccess,
            configDataFooter,
            validation,
            additionalDataQaSubmitButton,
            paramsComponent,
            implSubmit,
            buttonAfterSave,
        } = props;
        const history = useHistory();
        const params = useParams<'id' | any>();
        const { addToast } = useToast();
        const { setOpenLoading } = useContextGlobal();
        const [openModalDelete, setOpenModalDelete] = useState(false);
        const [width] = useWindowSize();
        const [indexes, setIndexes] = useState<Record<string, number>>({});
        const [open, setOpen] = useState<Record<string, boolean>>({});
        const [data, setData] = useState<any>(null);
        const [resultDataFooter, setResultDataFooter] = useState(null);

        const {
            handleSubmit,
            control,
            reset,
            setValue,
            getValues,
            watch,
            setError,
        } = useForm<T>({
            defaultValues,
        });

        const dynamicFieldArraysBase = inputsForm.reduce((acc, i) => {
            if (i.typeInput === 'subForm') {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                acc[i.name] = useFieldArray<T, any>({
                    control,
                    name: i.name as any,
                });
            }
            return acc;
        }, {} as Record<string, ReturnType<typeof useFieldArray>>);

        const dynamicFieldArrays = React.useMemo<any>(
            () => dynamicFieldArraysBase,
            [dynamicFieldArraysBase],
        );

        const dynamicUseFormToFieldArrays = inputsForm.reduce((acc, i) => {
            if (i.typeInput === 'subForm') {
                let defaultValues = {};
                if (i?.defaultValues) {
                    defaultValues = i.defaultValues;
                }
                // eslint-disable-next-line react-hooks/rules-of-hooks
                acc[i.name] = useForm<any>({
                    defaultValues: defaultValues,
                });
            }
            return acc;
        }, {} as Record<string, ReturnType<typeof useForm>>);

        const handleClick = useCallback((index: any, nameFields: string) => {
            setIndexes((prev: any) => ({ ...prev, [nameFields]: index }));
            setOpen((prev: any) => ({ ...prev, [nameFields]: true }));
        }, []);

        const inputs = React.useMemo<FormInputProps<T>[]>(
            () => [
                ...inputsForm.map(i => {
                    if (i.typeInput === 'subForm') {
                        i.index =
                            indexes[i.name] == null ? -1 : indexes[i.name];
                        const setIndex = (index: number) => {
                            setIndexes((prev: any) => ({
                                ...prev,
                                [i.name]: index,
                            }));
                        };
                        i.setIndex = setIndex;
                        i.getValues = getValues;

                        i.useDynamicFieldArray = dynamicFieldArrays[i.name];
                        i.useDynamicUseFormToFieldArrays =
                            dynamicUseFormToFieldArrays[i.name];

                        const openModal = open[i.name];
                        const setOpenModal = (open: boolean) => {
                            setOpen((prev: any) => ({
                                ...prev,
                                [i.name]: open,
                            }));
                        };
                        i.openModal = openModal;
                        i.setOpenModal = setOpenModal;
                    }
                    return { ...i, control, setValue };
                }),
            ],
            [
                inputsForm,
                control,
                setValue,
                indexes,
                getValues,
                open,
                dynamicFieldArrays,
            ],
        );

        const handleDeleteItemTable = useCallback(
            (index: number, nameFields: string) => {
                const fieldArrays = dynamicFieldArrays[nameFields];
                fieldArrays.remove(index);
            },
            [dynamicFieldArrays],
        );

        const setValueData = useCallback(
            (data: T) => {
                for (const input of inputsForm) {
                    if (input?.setData) {
                        setValue(input.name as Path<T>, input.setData(data));
                        continue;
                    }

                    setValue(input.name as Path<T>, data[input.name]);
                }
            },
            [inputsForm, setValue],
        );

        const setValueInput = useCallback(
            ({ value, field }: { value: any; field: string }) => {
                setValue(field as Path<T>, value);
            },
            [setValue],
        );

        // vai ter que passar no model
        const setModel = useCallback(
            (data: any) => {
                if (implSetValueData) {
                    implSetValueData(data);
                    return null;
                }
                setValueData(data);
                return null;
            },
            [implSetValueData, setValueData],
        );

        const handleNewItemTable = useCallback((nameFields: string) => {
            setIndexes((prev: any) => ({ ...prev, [nameFields]: -1 }));
            setOpen((prev: any) => ({ ...prev, [nameFields]: true }));
        }, []);

        React.useImperativeHandle(
            ref,
            () => ({
                setValue,
                getValues,
                handleClick,
                handleDeleteItemTable,
                handleNewItemTable,
                dynamicFieldArrays,
                setValueInput,
                watch,
                dynamicUseFormToFieldArrays,
                reset,
                data,
                setError,
            }),
            [
                setValue,
                getValues,
                handleClick,
                handleDeleteItemTable,
                handleNewItemTable,
                dynamicFieldArrays,
                setValueInput,
                watch,
                dynamicUseFormToFieldArrays,
                reset,
                data,
                setError,
            ],
        );

        useEffect(() => {
            let paramsForm = paramsComponent || params;
            if (paramsForm && paramsForm.id) {
                setOpenLoading(true);
                api.get(`${settings.apiPath}/${paramsForm.id}`)
                    .then(response => {
                        setData(response.data);
                        setModel(response.data);
                        setOpenLoading(false);
                    })
                    .catch(e => {
                        console.error(e);
                        setOpenLoading(false);
                        addToast({
                            type: 'error',
                            title: message.error.selectOne,
                        });
                    });
            }
        }, [params, paramsComponent]);

        useEffect(() => {
            if (configDataFooter?.dataFooter) {
                setResultDataFooter(
                    configDataFooter.dataFooter({
                        data: dynamicFieldArrays[configDataFooter.nameFields]
                            .fields,
                    }),
                );
            }
        }, [configDataFooter, dynamicFieldArrays]);

        const submit = async (data: any, e: any) => {
            if (implSubmit) {
                await implSubmit(data, e);
                return;
            }
            e.preventDefault();

            if (data['undefined']) {
                delete data['undefined'];
            }

            try {
                setOpenLoading(true);

                if (validation) {
                    const result = validation(data);
                    if (!result) {
                        setOpenLoading(false);
                        return;
                    }
                }

                for (const input of inputsForm) {
                    if (!input?.dto) {
                        continue;
                    }
                    data = input.dto(data);
                }

                const dataToSave = (({ generateInstallments, ...o }) => o)(
                    data,
                );

                let response = null;
                let paramsForm = paramsComponent || params;
                if (paramsForm && paramsForm.id) {
                    response = await api.patch(
                        `${settings.apiPath}/${paramsForm.id}`,
                        dataToSave,
                    );
                } else {
                    response = await api.post(
                        `${settings.apiPath}`,
                        dataToSave,
                    );
                }
                addToast({
                    type: 'success',
                    title: message.success.save,
                    description: '',
                });
                setOpenLoading(false);
                reset(defaultValues);

                if (!callbackSuccess) {
                    history.push(`${settings.pathList}?`);
                }

                if (callbackSuccess) {
                    if (data?.generateInstallments) {
                        response.data.generateInstallments =
                            data.generateInstallments;
                    }
                    callbackSuccess(response);
                }
            } catch (error) {
                setOpenLoading(false);
                const messagesResponse = handleExceptionMultipleMessages(error);
                let insideErrors = false;
                for (const messageResponse of messagesResponse) {
                    addToast({
                        type: 'error',
                        title: message.error.save,
                        description: messageResponse,
                    });
                    insideErrors = true;
                }
                if (!insideErrors) {
                    addToast({
                        type: 'error',
                        title: message.error.save,
                        description: 'Algo deu errado',
                    });
                }
            }
        };

        const handleCancel = () => {
            history.goBack();
        };

        const handleDelete = async (id: string) => {
            setOpenLoading(true);

            try {
                await api.delete(`${settings.apiPath}/${id}`);
                addToast({
                    type: 'success',
                    title: message.success.delete,
                    description: '',
                });
                setOpenLoading(false);
                history.push(`${settings.pathList}?`);
            } catch (error: any) {
                const { messageResponse, typeFeedback } = handleExceptionMessage(error);
                setOpenLoading(false);
                addToast({
                    type: typeFeedback,
                    title: message.error.delete,
                    description: messageResponse,
                });
            }
        };

        const handleConfirmeDelete = async (confirm: boolean) => {
            if (confirm) {
                setOpenModalDelete(false);
                handleDelete(params.id);
            } else {
                setOpenModalDelete(false);
            }
        };

        return (
            <div className="principal-container-form">
                <AlertDialog
                    handleConfirmation={handleConfirmeDelete}
                    open={openModalDelete}
                />
                <Paper
                    component={'div'}
                    sx={{
                        p: 2,
                        ...(settings?.boxShadow && {
                            boxShadow: settings.boxShadow,
                        }),
                    }}>
                    <div className="principal-container-form-header">
                        <div className="principal-container-form-header-title">
                            <Typography
                                variant="h6"
                                id="tableTitle"
                                component="h1">
                                {settings.title}
                            </Typography>
                        </div>
                        <div>
                            {params.id && (
                                <Tooltip title={'Excluir'}>
                                    <IconButton
                                        onClick={() => {
                                            setOpenModalDelete(true);
                                        }}>
                                        <IconComponent
                                            icon={icons.Delete}
                                            color="error"
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <form onSubmit={handleSubmit(submit)}>
                        <Grid container spacing={2}>
                            <RenderForm
                                inputsForm={inputs.filter(i => !i?.noRender)}
                                data={data}
                            />
                        </Grid>
                        <br />
                        <div className="info-footer">
                            <div className="footer-buttons">
                                <FormButton
                                    label={'Salvar'}
                                    typeButton={'submit'}
                                    additionalDataQa={
                                        additionalDataQaSubmitButton
                                    }
                                />
                                {buttonAfterSave && buttonAfterSave}
                                <FormButton
                                    label={'Voltar'}
                                    typeButton={'cancel'}
                                    onClick={() => handleCancel()}
                                />
                            </div>
                            {<AdditionalDataFooter data={resultDataFooter} />}
                        </div>
                    </form>
                    {inputs
                        .filter(
                            i => i.typeInput === 'subForm' && i.setOpenModal,
                        )
                        .map(data => {
                            const { append, update } =
                                data.useDynamicFieldArray;
                            return (
                                <SubFormComplete
                                    open={data?.openModal || false}
                                    setOpen={data?.setOpenModal}
                                    index={data.index == null ? -1 : data.index}
                                    setIndex={data?.setIndex}
                                    getValues={data?.getValues}
                                    width={width}
                                    inputs={data?.dataTable?.inputs || []}
                                    nameFields={data.name}
                                    update={update}
                                    append={append}
                                    useDynamicUseFormToFieldArrays={
                                        data.useDynamicUseFormToFieldArrays
                                    }
                                    defaultValues={data?.defaultValues}
                                    validationSubForm={data?.validationSubForm}
                                />
                            );
                        })}
                </Paper>
            </div>
        );
    },
);

export default FormComplete;
